// Library Methods
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

// Styles
import {
  getSurfaceTokens,
  getExamSliderButtonColors,
} from "src/styles/mui/muiStylesHelper";

type TableCardType = {
  variant?: "light" | "dark";
  children: React.ReactNode;
  isSelectedForViewing: boolean;
  isSelectedForDeletion: boolean;
  hasHoverState?: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  selectedColor?: string;
  selectedHoverColor?: string;
  backgroundColorOverride?: string;
  hoverColor?: string;
};

const TableCard = ({
  variant = "light",
  children,
  isSelectedForViewing,
  isSelectedForDeletion,
  hasHoverState = true,
  onClick,
  selectedColor = "#202338",
  selectedHoverColor = "#202338",
  backgroundColorOverride,
  hoverColor = "#4D5063",
}: TableCardType) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { onSurface } = getSurfaceTokens(theme);
  const examSliderButtonColors = getExamSliderButtonColors();

  const variantBackgroundColor = useMemo(() => {
    if (backgroundColorOverride) return backgroundColorOverride;

    switch (variant) {
      case "dark":
        return "#4D5063";
      default:
        return isDark ? examSliderButtonColors.background : "#FFF";
    }
  }, [variant, isDark, backgroundColorOverride, examSliderButtonColors]);

  const variantFontColor = useMemo(() => {
    switch (variant) {
      case "dark":
        return "#FFF";
      default:
        return isDark ? examSliderButtonColors.text : onSurface;
    }
  }, [variant, isDark, onSurface, examSliderButtonColors]);

  return (
    <Box
      sx={{
        position: "relative",
        padding: { xs: "8px", sm: "16px" },
        borderRadius: "8px",
        backgroundColor: isSelectedForViewing
          ? selectedColor
          : isSelectedForDeletion
            ? "#E2772E"
            : variantBackgroundColor,
        color:
          isSelectedForViewing || isSelectedForDeletion
            ? "#FFF"
            : variantFontColor,
        border:
          variant !== "dark" && isDark && isSelectedForViewing
            ? "1px solid #5B6D7A"
            : "none",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
        cursor: hasHoverState ? "pointer" : "default",
        "&:hover": {
          backgroundColor: isSelectedForViewing
            ? selectedHoverColor
            : isSelectedForDeletion
              ? "#CE631A"
              : hasHoverState
                ? hoverColor
                : variantBackgroundColor,
          color:
            hasHoverState || isSelectedForDeletion || isSelectedForViewing
              ? "#FFF"
              : variantFontColor,
        },
      }}
      onClick={onClick}
    >
      {children}
    </Box>
  );
};

export default TableCard;
